import { LanguageSettings } from '@wix/bookings-catalog-calendar-viewer-utils';
import {
  formatLocalDateTimeToDateView,
  formatLocalDateTimeToWeekday,
} from '@wix/bookings-catalog-calendar-viewer-utils/dateAndTime';
import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { AsyncComponentDataStatus } from '../../state/types';
import {
  extractSettingsKeys,
  ViewModelSubscription,
} from '../initViewModelSubscriptions';
import { WidgetViewModelFactory } from '../viewModel';
import {
  createAgendaItemViewModel,
  createDummyAgendaItemViewModel,
} from './agendaItemViewModel/agendaItemViewModel';

export type AgendaItemViewModel = {
  serviceName?: string;
  serviceImage?: { src: string; alt?: string };
  image?: any;
  startTime?: string;
  duration?: string;
  staffName?: string;
  staffImage?: string;
  location?: string;
  spots?: string;
  price?: string;
  buttonText?: string;
  disabled?: boolean;
  slotId: string;
};

export type SplittedDate = {
  date: string;
  day: string;
};

export type DaySectionViewModel = {
  _dateISO: string;
  date: string;
  splittedDate: SplittedDate;
  agendaItems: AgendaItemViewModel[];
};

export type AgendaViewModel = {
  days: DaySectionViewModel[];
  emptyState?: { title: string; subtitle: string };
};

export const createAgendaViewModel: WidgetViewModelFactory<
  AgendaViewModel | undefined
> = ({ state, flowAPI }) => {
  const { slots, services, languageSettings } = state;
  const {
    environment: { isEditor },
    translations: { t },
  } = flowAPI;
  if (
    slots.status === AsyncComponentDataStatus.LOADING ||
    services.status === AsyncComponentDataStatus.LOADING
  ) {
    return;
  }

  if (
    isEditor &&
    slots.status === AsyncComponentDataStatus.IDLE &&
    !slots.data.length
  ) {
    return createDummyAgendaViewModel({ flowAPI, languageSettings });
  }

  if (
    slots.status === AsyncComponentDataStatus.ERROR ||
    services.status === AsyncComponentDataStatus.ERROR
  ) {
    if (isEditor) {
      return createDummyAgendaViewModel({ flowAPI, languageSettings });
    }

    return {
      days: [],
      emptyState: {
        title: t('daily-agenda.errors.no-availability-main'),
        subtitle: t('daily-agenda.errors.no-availability-subtitle'),
      },
    };
  }

  const timeSlots = slots.data;
  if (!timeSlots || timeSlots.length === 0) {
    return {
      days: [],
      emptyState: {
        title: t('daily-agenda.errors.no-availability-main'),
        subtitle: t('daily-agenda.errors.no-availability-subtitle'),
      },
    };
  }

  const groupedSlotsByDays = new Map<
    string,
    { _dateISO: string; agendaItems: AgendaItemViewModel[] }
  >();
  const splittedDates = new Map<string, SplittedDate>();
  timeSlots.forEach((slot) => {
    const formattedDate = formatLocalDateTimeToDateView(slot.localStartDate!);
    const formattedWeekday = formatLocalDateTimeToWeekday(
      slot.localStartDate!,
      languageSettings.dateRegionalSettingsLocale,
    );

    const localDate = new Date(slot.localStartDate!);
    const dateFormatter = new Intl.DateTimeFormat(
      languageSettings.dateRegionalSettingsLocale,
      {
        day: 'numeric',
        month: 'long',
      },
    );

    const splittedDate: SplittedDate = {
      date: dateFormatter.format(localDate),
      day: formattedWeekday,
    };

    const agendaItem = createAgendaItemViewModel({
      slot,
      state,
      flowAPI,
    });

    if (groupedSlotsByDays.has(formattedDate)) {
      groupedSlotsByDays.get(formattedDate)?.agendaItems.push(agendaItem);
    } else {
      groupedSlotsByDays.set(formattedDate, {
        _dateISO: new Date(slot.localStartDate!).toISOString(),
        agendaItems: [agendaItem],
      });
    }

    if (!splittedDates.has(formattedDate)) {
      splittedDates.set(formattedDate, splittedDate);
    }
  });

  const days: DaySectionViewModel[] = Array.from(
    groupedSlotsByDays.entries(),
  ).map(([date, { _dateISO, agendaItems }]) => ({
    _dateISO,
    date,
    splittedDate: splittedDates.get(date)!,
    agendaItems,
  }));

  return {
    days,
  };
};

export const subscribeToAgendaViewModel: ViewModelSubscription = ({
  subscribe,
  setViewModel,
  flowAPI,
  getState,
}) => {
  subscribe(
    (state) => ({
      slotsStatus: state.slots.status,
      servicesStatus: state.services.status,
      staffMembersStatus: state.staffMembers.status,
      slotsMetaDataStatus: state.slotsMetaData.status,
      bookedSlotsMetaDataStatus: state.bookedSlotsMetaData.status,
      bookedSlotsStatus: state.bookedSlots.status,
      ...extractSettingsKeys(state, [
        'bookButtonText',
        'bookOnlineDisabledIndication',
        'joinWaitlistButton',
        'pendingApprovalButton',
        'toEarlyToBookIndication',
        'toLateToBookIndication',
        'noSpotsLeft',
        'alreadyBookedButton',
      ]),
    }),
    () => {
      const agendaViewModel = createAgendaViewModel({
        state: getState(),
        flowAPI,
      });
      setViewModel({ agendaViewModel });
    },
  );
};

export const createDummyAgendaViewModel = ({
  flowAPI,
  languageSettings,
}: {
  flowAPI: ControllerFlowAPI;
  languageSettings: LanguageSettings;
}): AgendaViewModel => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const createDummyDateFormat = (date: Date): SplittedDate => {
    const dateFormatter = new Intl.DateTimeFormat(
      languageSettings.dateRegionalSettingsLocale,
      {
        day: 'numeric',
        month: 'long',
      },
    );
    return {
      date: dateFormatter.format(date),
      day: new Intl.DateTimeFormat(
        languageSettings.dateRegionalSettingsLocale,
        {
          weekday: 'long',
        },
      ).format(date),
    };
  };

  return {
    days: [
      {
        _dateISO: today.toISOString(),
        date: formatLocalDateTimeToDateView(today.toISOString()),
        splittedDate: createDummyDateFormat(today),
        agendaItems: [
          createDummyAgendaItemViewModel({ flowAPI, languageSettings }),
          createDummyAgendaItemViewModel({
            flowAPI,
            languageSettings,
            overrideStartTime: '2025-04-07T10:00:00',
          }),
        ],
      },
      {
        _dateISO: tomorrow.toISOString(),
        date: formatLocalDateTimeToDateView(tomorrow.toISOString()),
        splittedDate: createDummyDateFormat(tomorrow),
        agendaItems: [
          createDummyAgendaItemViewModel({ flowAPI, languageSettings }),
          createDummyAgendaItemViewModel({
            flowAPI,
            languageSettings,
            overrideStartTime: '2025-04-07T14:00:00',
          }),
        ],
      },
    ],
  };
};
